import React from 'react';
import styled from '@emotion/styled';
import { Dropdown } from 'react-bootstrap';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { FaInfoCircle, FaArchive, FaTrash } from 'react-icons/fa';
import useUser from 'hooks/useUser';
import {
  getStateLabel,
  getStatusLabel,
} from 'helpers/forms';
import { formatUTCDate } from 'utils/dateTime';
import { appRoles, ProgramTypeAndSubTypeLabel, abaVerificationTypeLabels } from 'utils/enums';
import { ApplicationState, ApplicationStatus } from 'types/application';

const HeaderContainer = styled.div`
  background-color: #e9ecef;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border-radius: 4px;
`;

const HeaderTitle = styled.h4`
  margin: 0 0 1rem 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #212529;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 2rem;
`;

const HeaderInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem 2rem;
  flex: 1;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const InfoLabel = styled.span`
  font-size: 0.875rem;
  color: #6c757d;
  font-weight: 500;
`;

const InfoValue = styled.span`
  font-size: 0.95rem;
  color: #212529;
`;

const MenuButton = styled(Dropdown.Toggle)`
  background: transparent !important;
  border: none !important;
  color: #6c757d !important;
  padding: 0.25rem !important;
  box-shadow: none !important;
  
  &:hover, &:focus, &:active {
    color: #495057 !important;
    background: transparent !important;
  }
  
  &::after {
    display: none;
  }
`;

const MenuItemIcon = styled.span`
  margin-right: 0.5rem;
  width: 1rem;
  display: inline-block;
`;

const MenuItemOrange = styled(Dropdown.Item)`
  color: #fd7e14;
  
  &:hover {
    background-color: #fff3e0;
    color: #fd7e14;
  }
`;

const MenuItemRed = styled(Dropdown.Item)`
  color: #dc3545;
  
  &:hover {
    background-color: #ffe0e0;
    color: #dc3545;
  }
`;

interface VerificationHeaderProps {
  burnCenterName: string;
  hospitalName?: string;
  isSample?: boolean;
  city?: string;
  location?: string;
  applicationType: string;
  applicationPopulation?: string | null;
  state: ApplicationState;
  status: ApplicationStatus | null;
  requestedAt: string | null;
  siteVisitAt: string | null;
  expiredAt: string | null;
  archivedAt?: string | null;
  onVersionInfo?: () => void;
  onArchive?: () => void;
  onUnarchive?: () => void;
  onDelete?: () => void;
}

const getPopulationLabel = (population?: string | null): string | null => {
  if (!population) {
    return null;
  }

  return abaVerificationTypeLabels[
    population as keyof typeof abaVerificationTypeLabels
  ] ?? null;
};

const VerificationHeader: React.FC<VerificationHeaderProps> = ({
  burnCenterName,
  hospitalName,
  city,
  location,
  applicationType,
  applicationPopulation,
  state,
  status,
  requestedAt,
  siteVisitAt,
  expiredAt,
  archivedAt,
  isSample,
  onVersionInfo,
  onArchive,
  onUnarchive,
  onDelete,
}) => {
  const { userRoles } = useUser();
  const isAdmin = userRoles.some((role: string) => [appRoles.abaADMIN,
    appRoles.bdataADMIN].includes(role));

  const title = hospitalName && city && location
    ? `${burnCenterName} (${hospitalName}), ${city}, ${location}`
    : burnCenterName;
  const isArchived = !!archivedAt;
  const populationLabel = getPopulationLabel(applicationPopulation);

  return (
    <HeaderContainer>
      <HeaderTitle>{title}</HeaderTitle>
      <HeaderContent>
        <HeaderInfo>
          <InfoItem>
            <InfoLabel>Type:</InfoLabel>
            <InfoValue>
              {applicationType ? (ProgramTypeAndSubTypeLabel[applicationType as keyof typeof ProgramTypeAndSubTypeLabel] || applicationType) : ''}
            </InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Application Date:</InfoLabel>
            <InfoValue>{formatUTCDate(requestedAt)}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Final Determination:</InfoLabel>
            <InfoValue>{getStatusLabel(status)}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Review Status:</InfoLabel>
            <InfoValue>{getStateLabel(state)}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Site Visit Date:</InfoLabel>
            <InfoValue>{formatUTCDate(siteVisitAt)}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Expiration Date:</InfoLabel>
            <InfoValue>{formatUTCDate(expiredAt)}</InfoValue>
          </InfoItem>
          {isSample && (
          <InfoItem>
            <InfoLabel>Sample Application:</InfoLabel>
            <InfoValue>Yes</InfoValue>
          </InfoItem>
          )}
          {populationLabel && (
          <InfoItem>
            <InfoLabel>Population:</InfoLabel>
            <InfoValue>{populationLabel}</InfoValue>
          </InfoItem>
          )}
        </HeaderInfo>
        {isAdmin && (
          <Dropdown align="end">
            <div className="d-flex align-items-center gap-2">
              {isArchived && (
                <small>{`Archived on ${formatUTCDate(archivedAt)}`}</small>
              )}
              <MenuButton variant="link" id="verification-menu">
                <BsThreeDotsVertical size={24} />
              </MenuButton>
            </div>

            <Dropdown.Menu>
              {onVersionInfo && (
                <Dropdown.Item onClick={onVersionInfo}>
                  <MenuItemIcon>
                    <FaInfoCircle />
                  </MenuItemIcon>
                  Version Information
                </Dropdown.Item>
              )}
              {!isArchived && onArchive && (
                <MenuItemOrange onClick={onArchive}>
                  <MenuItemIcon>
                    <FaArchive />
                  </MenuItemIcon>
                  Archive
                </MenuItemOrange>
              )}
              {isArchived && onUnarchive && (
                <MenuItemOrange onClick={onUnarchive}>
                  <MenuItemIcon>
                    <FaArchive />
                  </MenuItemIcon>
                  Unarchive
                </MenuItemOrange>
              )}
              {!isArchived && onDelete && (
                <MenuItemRed onClick={onDelete}>
                  <MenuItemIcon>
                    <FaTrash />
                  </MenuItemIcon>
                  Delete
                </MenuItemRed>
              )}
            </Dropdown.Menu>
          </Dropdown>
        )}
      </HeaderContent>
    </HeaderContainer>
  );
};

export default VerificationHeader;
